import { isAfter } from 'date-fns';
import type { OauthDataObject } from 'models/service';

import type { Oauth } from '../../models';
import type { ApiClientResponse } from './api';
import apiClient from './api-client';
import { routes } from './routes';

type QueueItem<T = undefined> = {
  reject: (error: Error | null) => void;
  resolve: (result: T) => void;
};

type TokenType = 'accessToken' | 'accessTokenCreatedAt' | 'accessTokenExpiresIn' | 'refreshToken';

let isRefreshingToken = false;
let tokenWaitingQueue: Array<QueueItem<OauthDataObject>> = [];

const processRefreshingQueue = (data: null | OauthDataObject, error: Error | null = null) => {
  for (const prom of tokenWaitingQueue) {
    data ? prom.resolve(data) : prom.reject(error);
  }

  tokenWaitingQueue = [];
};

const pushToGetTokenQueue = async () =>
  new Promise<OauthDataObject>((resolve, reject) => {
    tokenWaitingQueue.push({ reject, resolve });
  });

const refreshTokenOnce = async (refreshToken: string) => {
  if (isRefreshingToken) {
    return pushToGetTokenQueue();
  }

  try {
    isRefreshingToken = true;
    const response = await getRailsRefreshToken(refreshToken);
    processRefreshingQueue(response.data);
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  } catch (error: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    processRefreshingQueue(null, error);
    throw error;
  } finally {
    // eslint-disable-next-line require-atomic-updates -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    isRefreshingToken = false;
  }
};

const getToken = (type: TokenType) => window.localStorage.getItem(type);
const setToken = (type: TokenType, token = '') => {
  window.localStorage.setItem(type, token);
};

export const clearRailsTokens = () => {
  setToken('accessToken', '');
  setToken('refreshToken', '');
  setToken('accessTokenExpiresIn', '');
  setToken('accessTokenCreatedAt', '');
};

export const storeRailsTokensData = (data: Oauth) => {
  setToken('accessToken', data.accessToken);
  setToken('refreshToken', data.refreshToken);
  setToken('accessTokenExpiresIn', String(data.expiresIn));
  setToken('accessTokenCreatedAt', String(data.createdAt));
};

export const getRailsTokensData = () => {
  const accessToken = getToken('accessToken');
  const refreshToken = getToken('refreshToken');
  const accessTokenExpiresIn = getToken('accessTokenExpiresIn');
  const accessTokenCreatedAt = getToken('accessTokenCreatedAt');

  if (accessToken && refreshToken && accessTokenExpiresIn && accessTokenCreatedAt) {
    return {
      accessToken,
      accessTokenCreatedAt: Number(accessTokenCreatedAt),
      accessTokenExpiresIn: Number(accessTokenExpiresIn),
      refreshToken,
    };
  }

  return null;
};

export const getRailsAccessToken = async () => {
  const oauthData = getRailsTokensData();

  if (!oauthData) {
    return null;
  }

  const expiration = new Date((oauthData.accessTokenCreatedAt + oauthData.accessTokenExpiresIn - 60) * 1000);
  const now = new Date(Date.now());

  if (isAfter(now, expiration)) {
    try {
      const tokenData = await refreshTokenOnce(oauthData.refreshToken);
      storeRailsTokensData({
        accessToken: tokenData.access_token,
        createdAt: tokenData.created_at,
        expiresIn: tokenData.expires_in,
        refreshToken: tokenData.refresh_token,
      });

      return tokenData.access_token;
    } catch (error) {
      // Refresh was not success, pass old token and continue
      console.error(error);
      return oauthData.accessToken;
    }
  }

  return oauthData.accessToken;
};

/* eslint-disable @typescript-eslint/naming-convention -- Start: We missing correct application model in camelCase, we need to fix it */
const getRailsRefreshToken = async (refreshToken: string) =>
  apiClient.post<OauthDataObject, ApiClientResponse<OauthDataObject>>(
    routes.oauth.token,
    {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    },
    {
      skipAuth: true,
    },
  );
/* eslint-enable @typescript-eslint/naming-convention -- End: We missing correct application model in camelCase, we need to fix it */
